import { useCallback, useEffect, useMemo, useRef, type ClipboardEvent } from 'react';
import { type CellKeyDownEvent, type ColDef, type GridApi, type GridReadyEvent, type ICellRendererParams, type RowDragEndEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { centroAceitaEtapa, type LinhaSimuladaResponse } from '@triade/contracts';
import { useEdicaoDeGrade } from '@/lib/edicao-pendente';
import { formatarCusto } from '@/lib/format';
import { X } from 'lucide-react';
import { SO_AO_SALVAR } from './erros-salvar';
import { acharCentro, atualizarSemEditar, Chip, comMarcaDeErro, ctx, DIREITA, EditorCentro, Etiqueta, rotuloCentro, setter, TEMA, type ContextoGrade } from './grade-comum';
import { aplicarColagem, COLUNAS_ETAPA, lerColagem, mesCurto, ROTULO_ALERTA, ROTULO_AVISO_LINHA, type CentroOpcao, type LinhaFicha } from './tipos';

/** Chips de uma etapa: alertas e avisos que a API devolveu na simulacao; o CPM antigo vem do gravado. */
function ChipsEtapa(p: ICellRendererParams<LinhaFicha>) {
  const sim: LinhaSimuladaResponse | undefined = p.data ? ctx(p).calculo?.get(p.data.uid) : undefined;
  const avisos = (sim?.avisos ?? []).filter((a) => !SO_AO_SALVAR.has(a));
  const alertas = (sim?.alertas ?? []).filter((a) => !SO_AO_SALVAR.has(a));
  const antigo = p.data?.etapaGravada?.cpmAntigo && avisos.length === 0 ? p.data.etapaGravada : undefined;
  if (alertas.length === 0 && avisos.length === 0 && !antigo) return null;
  return (
    <span className="flex flex-wrap items-center gap-1" data-testid="alertas-etapa">
      {alertas.map((a) => (
        <Chip key={a}>{ROTULO_ALERTA[a]}</Chip>
      ))}
      {avisos.map((a) => (
        <Chip key={a} tom={a === 'CPM_AUSENTE' || a === 'CENTRO_INVALIDO' || a === 'ETAPA_TIPO_INVALIDO' ? 'erro' : 'aviso'} testid={a === 'CPM_ANTIGO' ? 'chip-cpm-antigo' : undefined}>
          {ROTULO_AVISO_LINHA[a]}
          {a === 'CPM_ANTIGO' && p.data?.etapaGravada?.competencia ? ` (${mesCurto(p.data.etapaGravada.competencia)})` : ''}
        </Chip>
      ))}
      {antigo && (
        <Chip testid="chip-cpm-antigo">CPM antigo ({mesCurto(antigo.competencia)})</Chip>
      )}
    </span>
  );
}

/** Centro analitico com o caminho na frente ("3.1 · PRENSAGEM"); sem centro = a escolher (a API recusa ao gravar). */
function CentroEtapa(p: ICellRendererParams<LinhaFicha>) {
  const id = p.data?.centroCustoId;
  if (!id) return <span className="text-danger-ink">Escolher centro</span>;
  const c = ctx(p).centros.find((x) => x.id === id);
  if (!c) return <span className="text-danger-ink">Centro indisponível</span>;
  return (
    <span className="flex min-w-0 items-center gap-2" title={rotuloCentro(c)}>
      <Etiqueta title="Caminho do centro">{c.caminho}</Etiqueta>
      <span className="truncate">{c.nome}</span>
    </span>
  );
}

/** Descricao da etapa com os alertas ao lado (um so campo: a coluna larga e a descricao). */
function DescricaoEtapa(p: ICellRendererParams<LinhaFicha>) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="truncate">{p.value}</span>
      <ChipsEtapa {...p} />
    </span>
  );
}

/** CPM vigente na data como a API gravou (SOMENTE LEITURA) e a competencia. Linha alterada ou nova: "…" ate salvar. */
function CpmEtapa(p: ICellRendererParams<LinhaFicha>) {
  const l = p.data;
  if (!l?.centroCustoId) return <span className="text-ink-disabled">—</span>;
  if (!l.etapaGravada) return <span className="tabular text-ink-muted">…</span>;
  if (!l.etapaGravada.cpm) return <span className="text-danger-ink">Sem CPM</span>;
  return (
    <span className="flex flex-col items-end leading-tight">
      <span className="tabular">{formatarCusto(l.etapaGravada.cpm)}</span>
      {l.etapaGravada.competencia && <span className="text-caption text-ink-muted">{mesCurto(l.etapaGravada.competencia)}</span>}
    </span>
  );
}

function Remover(p: ICellRendererParams<LinhaFicha>) {
  if (!p.data || !ctx(p).editavel) return null;
  const uid = p.data.uid;
  const n = (p.node.rowIndex ?? 0) + 1;
  return (
    <button
      type="button"
      aria-label={`Remover etapa ${n}`}
      title="Remover etapa"
      tabIndex={-1}
      onClick={() => ctx(p).remover(uid)}
      className="inline-flex size-7 items-center justify-center rounded-md text-ink-muted hover:bg-line-soft hover:text-danger-ink"
    >
      <X aria-hidden="true" className="size-3.5" />
    </button>
  );
}

/**
 * Edicao de uma etapa: centro so aceita analitico escolhido entre `centros` (os que aceitam etapa); vazio nao tira o centro.
 * Sessao 37: a etapa se mostra como "<ordem>. <centro>"; o campo de texto da linha e so a OBSERVACAO (opcional, vazia por padrao) e
 * nunca e preenchido com o nome do centro (desfaz a S24 5b). `todos` fica por compatibilidade de chamada.
 */
export function aplicarEdicaoEtapa(
  linha: LinhaFicha,
  campo: string,
  valor: string,
  centros: ReadonlyArray<CentroOpcao>,
  _todos: ReadonlyArray<CentroOpcao> = centros,
): LinhaFicha {
  if (campo === 'centroCustoId') {
    if (!valor) return linha; // a etapa precisa de centro: a API recusa a linha sem ele (MAO_DE_OBRA_FORA_DO_PROCESSO)
    const c = centros.find((x) => x.id === valor) ?? acharCentro(centros, valor);
    if (!c) return linha;
    return { ...linha, centroCustoId: c.id, unidade: 'min', etapaGravada: undefined };
  }
  if (campo === 'quantidade') return { ...linha, quantidade: valor, etapaGravada: undefined };
  if (campo === 'descricao') return { ...linha, descricao: valor };
  return linha;
}

/** Colunas da secao Processo. `comCusto` = custo.ver: CPM e custo da etapa so existem com R$ (PCP nao as recebe). */
function colunas(comCusto: boolean, editavelGrade: boolean): ColDef<LinhaFicha>[] {
  const editavel = (p: { context: unknown }) => ctx(p).editavel;
  const todas: ColDef<LinhaFicha>[] = [
    {
      // Sessao 37: ORDEM da etapa (1, 2, 3...), reorganizavel arrastando a linha pela alca (ou Alt+seta para cima/baixo). A etapa se le "<ordem>. <centro>".
      colId: 'ordem',
      headerName: 'Ordem',
      width: 84,
      suppressNavigable: true,
      rowDrag: editavelGrade,
      valueGetter: (p) => (p.node?.rowIndex ?? 0) + 1,
      cellClass: 'tabular',
    },
    {
      colId: 'centro',
      headerName: 'Centro analítico',
      width: 200,
      editable: editavel,
      cellEditor: EditorCentro,
      valueGetter: (p) => p.data?.centroCustoId ?? null,
      valueSetter: setter('centroCustoId'),
      cellRenderer: CentroEtapa,
      tooltip: (p) => (p.data?.centroCustoId ? 'Etapa: minutos x CPM do centro na data' : 'Escolha um centro analítico'),
    },
    {
      colId: 'descricao',
      headerName: 'Observação (opcional)',
      width: 280,
      minWidth: 280,
      editable: editavel,
      valueGetter: (p) => p.data?.descricao,
      valueSetter: setter('descricao'),
      cellRenderer: DescricaoEtapa,
    },
    {
      colId: 'quantidade',
      headerName: 'Minutos',
      width: 88,
      editable: editavel,
      valueGetter: (p) => p.data?.quantidade,
      valueSetter: setter('quantidade'),
      ...DIREITA,
    },
    {
      colId: 'cpm',
      headerName: 'CPM na data',
      width: 128,
      suppressNavigable: true,
      valueGetter: (p) => p.data?.etapaGravada?.cpm ?? null,
      cellRenderer: CpmEtapa,
      ...DIREITA,
    },
    {
      colId: 'custoEtapa',
      headerName: 'Custo da etapa',
      width: 118,
      suppressNavigable: true,
      // Gravado (a etapa como a API a gravou). Linha alterada: o que a API simulou (minutos x CPM); sem nenhum, "…".
      valueGetter: (p) => {
        if (!p.data?.centroCustoId) return '';
        if (p.data.etapaGravada) return p.data.etapaGravada.custo ?? '…';
        const sim = ctx(p).calculo?.get(p.data.uid);
        return sim?.custoTotal ?? '…';
      },
      valueFormatter: (p) => (p.value === '…' || p.value === '' ? String(p.value ?? '') : formatarCusto(String(p.value))),
      ...DIREITA,
    },
    { colId: 'acoes', headerName: '', width: 36, suppressNavigable: true, cellRenderer: Remover },
  ];
  const semR$ = new Set(['cpm', 'custoEtapa']);
  return comCusto ? todas : todas.filter((c) => !semR$.has(c.colId ?? ''));
}

export interface GradeProcessoProps {
  /** So as etapas (linhas da secao Processo). */
  linhas: LinhaFicha[];
  /** Resultado da simulacao (API) por uid: custo da etapa e avisos de CPM/centro. */
  calculo: ReadonlyMap<string, LinhaSimuladaResponse>;
  editavel: boolean;
  comCusto: boolean;
  centros: ReadonlyArray<CentroOpcao>;
  /** Celulas a marcar por linha (uid) apos a validacao ao salvar. */
  erros?: ReadonlyMap<string, ReadonlySet<string>>;
  onChange: (linhas: LinhaFicha[]) => void;
  focar?: { uid: string; n: number } | null;
  apiRef?: React.MutableRefObject<GridApi<LinhaFicha> | null>;
}

/**
 * Secao "Processo produtivo" (AG Grid Community, edicao tipo planilha): centro analitico (combobox so de analiticos, com o
 * caminho), minutos editaveis, CPM vigente na data e custo da etapa (SOMENTE LEITURA, da API). Nada e calculado aqui.
 */
export function GradeProcesso({ linhas, calculo, editavel, comCusto, centros, erros, onChange, focar, apiRef }: GradeProcessoProps) {
  const api = useRef<GridApi<LinhaFicha> | null>(null);
  const edicaoGrade = useEdicaoDeGrade();
  const linhasRef = useRef(linhas);
  linhasRef.current = linhas;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Etapa so aceita centro analitico, ativo, produtivo e de transformacao (a API recusa o resto com 422 CENTRO_NAO_PRODUTIVO).
  // O analitico ja vem sem inativos (useAnaliticos(false)), por isso ativo: true aqui.
  const opcoesEtapa = useMemo(
    () => centros.filter((c) => centroAceitaEtapa({ tipoNo: 'ANALITICO', natureza: c.natureza, classificacao: c.classificacao, ativo: true })),
    [centros],
  );

  const contexto = useMemo<ContextoGrade>(
    () => ({
      calculo,
      editavel,
      centros,
      opcoesCentro: opcoesEtapa,
      ...(erros ? { erros } : {}),
      editar: (uid, campo, valor) =>
        onChangeRef.current(linhasRef.current.map((l) => (l.uid === uid ? aplicarEdicaoEtapa(l, campo, valor, opcoesEtapa, centros) : l))),
      remover: (uid) => onChangeRef.current(linhasRef.current.filter((l) => l.uid !== uid)),
    }),
    [calculo, editavel, centros, opcoesEtapa, erros],
  );

  const defs = useMemo(() => comMarcaDeErro(colunas(comCusto, editavel)), [comCusto, editavel]);

  // A API respondeu (ou a edicao confirmou): redesenha o que depende da simulacao, sem interromper uma edicao em curso.
  useEffect(() => {
    atualizarSemEditar(api.current, ['custoEtapa', 'descricao']);
  }, [calculo]);
  // A ordem e a posicao da linha: redesenha quando as linhas mudam (arrastar, adicionar, remover).
  useEffect(() => {
    atualizarSemEditar(api.current, ['ordem']);
  }, [linhas]);

  useEffect(() => {
    if (!api.current) return;
    api.current.setGridOption('context', contexto);
    atualizarSemEditar(api.current);
  }, [contexto]);

  useEffect(() => {
    if (!focar || !api.current) return;
    const id = setTimeout(() => {
      const a = api.current;
      if (!a) return;
      const indice = linhasRef.current.findIndex((l) => l.uid === focar.uid);
      if (indice < 0) return;
      a.ensureIndexVisible(indice);
      a.setFocusedCell(indice, 'centro');
      a.startEditingCell({ rowIndex: indice, colKey: 'centro' });
    }, 0);
    return () => clearTimeout(id);
  }, [focar]);

  // A coluna da descricao (Etapa / Componente) leva o espaco que sobra no wrapper, com minimo de 280px. Largura por
  // setColumnWidths (e nao flex): o AG Grid nao recalcula colunas flex quando a janela muda, e a ultima coluna sairia do cartao.
  const largura = useRef<HTMLDivElement>(null);
  const ajustarDescricao = useCallback(() => {
    const a = api.current;
    const alvo = largura.current;
    if (!a || !alvo) return;
    const fixas = (a.getColumns() ?? []).filter((c) => c.getColId() !== 'descricao').reduce((soma, c) => soma + c.getActualWidth(), 0);
    a.setColumnWidths([{ key: 'descricao', newWidth: Math.max(280, alvo.clientWidth - fixas - 2) }]);
  }, []);
  useEffect(() => {
    const alvo = largura.current;
    if (!alvo || typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(() => ajustarDescricao());
    observador.observe(alvo);
    return () => observador.disconnect();
  }, [ajustarDescricao]);
  useEffect(() => ajustarDescricao(), [defs, ajustarDescricao]);

  // Arrastar a linha pela alca reordena as etapas: a ordem nova e a da grade depois do arrasto.
  const aoSoltarLinha = useCallback((e: RowDragEndEvent<LinhaFicha>) => {
    const novas: LinhaFicha[] = [];
    e.api.forEachNode((n) => {
      if (n.data) novas.push(n.data);
    });
    if (novas.length === linhasRef.current.length && novas.some((l, i) => l.uid !== linhasRef.current[i]?.uid)) onChangeRef.current(novas);
  }, []);

  const aoTeclar = useCallback(
    (e: CellKeyDownEvent<LinhaFicha>) => {
      const ev = e.event as KeyboardEvent | null;
      // Alt + seta para cima/baixo move a etapa focada (alternativa ao arrasto, para quem usa so o teclado).
      if (ev && editavel && ev.altKey && !ev.ctrlKey && !ev.metaKey && (ev.key === 'ArrowUp' || ev.key === 'ArrowDown') && e.data && e.api.getEditingCells().length === 0) {
        ev.preventDefault();
        const atual = linhasRef.current;
        const de = atual.findIndex((l) => l.uid === e.data!.uid);
        const para = ev.key === 'ArrowUp' ? de - 1 : de + 1;
        if (de < 0 || para < 0 || para >= atual.length) return;
        const novas = [...atual];
        [novas[de], novas[para]] = [novas[para] as LinhaFicha, novas[de] as LinhaFicha];
        onChangeRef.current(novas);
        return;
      }
      if (!ev || !editavel || ev.ctrlKey || ev.metaKey || ev.altKey) return;
      if (ev.key !== 'Delete' && ev.key !== 'Backspace') return;
      if (e.api.getEditingCells().length > 0) return;
      const col = e.column.getColId();
      if (!e.data || (col !== 'descricao' && col !== 'quantidade')) return;
      ev.preventDefault();
      contexto.editar(e.data.uid, col, '');
    },
    [contexto, editavel],
  );

  // Colar do Excel: o bloco entra a partir da celula focada (descricao ou minutos); linhas alem do fim viram etapas.
  const aoColar = (e: ClipboardEvent<HTMLDivElement>) => {
    const a = api.current;
    if (!editavel || !a || a.getEditingCells().length > 0) return;
    const foco = a.getFocusedCell();
    const col = foco?.column.getColId();
    if (!foco || !col || !(COLUNAS_ETAPA as readonly string[]).includes(col)) return;
    const matriz = lerColagem(e.clipboardData.getData('text/plain'));
    if (matriz.length === 0) return;
    e.preventDefault();
    onChange(aplicarColagem(linhas, foco.rowIndex, col, matriz, 'processo'));
  };

  const aoPronta = (e: GridReadyEvent<LinhaFicha>) => {
    api.current = e.api;
    if (apiRef) apiRef.current = e.api;
    ajustarDescricao();
  };

  return (
    <div ref={largura} data-testid="grade-processo" onPaste={aoColar} className="w-full">
      <AgGridReact<LinhaFicha>
        theme={TEMA}
        rowData={linhas}
        columnDefs={defs}
        context={contexto}
        getRowId={(p) => p.data.uid}
        onGridReady={aoPronta}
        {...edicaoGrade}
        onCellKeyDown={aoTeclar}
        rowDragManaged
        onRowDragEnd={aoSoltarLinha}
        domLayout="autoHeight"
        suppressColumnVirtualisation
        enterNavigatesVertically
        enterNavigatesVerticallyAfterEdit
        stopEditingWhenCellsLoseFocus
        suppressMovableColumns
        suppressCellFocus={false}
        tooltipShowDelay={400}
        defaultColDef={{ sortable: false, resizable: false, suppressHeaderMenuButton: true }}
      />
    </div>
  );
}
